import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  downloadRezeptInvoiceListPdf,
  type RezeptInvoiceListResponse,
  type RezeptInvoiceListPdfPayload,
} from '@/services/main/rezept';
import { useClinicContext } from './useClinicContext';

/**
 * Mutation hook for 領収書・請求書 `一覧印刷` (bulk receipt/invoice PDF).
 * Returns a preview URL; caller is responsible for opening it in a new window.
 * Updates the cached list immediately so print_invoice status refreshes before
 * the preview tab finishes rendering, then invalidates for eventual sync.
 */
export const useRezeptInvoiceListPdf = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RezeptInvoiceListPdfPayload) =>
      downloadRezeptInvoiceListPdf(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: (_url, payload) => {
      const printedIds = new Set((payload.rezept_ids ?? []).map(Number));
      if (printedIds.size > 0) {
        queryClient.setQueriesData<RezeptInvoiceListResponse>(
          { queryKey: ['rezept-invoice', 'list'] },
          old => {
            if (!old?.data?.data) return old;
            return {
              ...old,
              data: {
                ...old.data,
                data: old.data.data.map(row =>
                  printedIds.has(Number(row.id)) ? { ...row, print_invoice: 1 } : row
                ),
              },
            };
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['rezept-invoice', 'list'] });
    },
  });
};

export default useRezeptInvoiceListPdf;
